<template>
    <DModal
        :title="__('administrations.sanity_checks.modals.title')"
        :show="showModal"
        is-medium
        @close="$emit('close-modal')"
    >
        <template #header>
            <DToaster
                v-if="isSuccessAlertVisible()"
                placement="top-end"
                visible
            >
                <DToast
                    :key="'flash'"
                    color="success"
                    :visible="true"
                    :header-text="__('messages.success')"
                    :body-text="selectedAffectedObject?.message"
                    @close="resetSelectedItem()"
                />
            </DToaster>
        </template>
        <DCard padding>
            <p>
                {{ sanityCheck.description }}
            </p>
            <p v-if="affectedObjectData">
                {{ __('administrations.sanity_checks.modals.subtitle', {count: affectedObjectData.length}) }}
            </p>
            <DTable
                :items="affectedObjectData"
                :fields="fields"
            >
                <template #class="{item}">
                    <td v-if="classLinkable(item)">
                        <Link
                            :href="classLink(item)"
                        >
                            {{ item.class }} {{ item.id }}
                        </Link>
                    </td>
                    <td v-else>
                        {{ item.class }} {{ item.id }}
                    </td>
                    <p v-if="modalHasErrors" />
                </template>
                <template #actions="{item}">
                    <td>
                        <div>
                            <DDropdown
                                :id="`action_dropdown_${item.id}`"
                                icon="dots-horizontal"
                            >
                                <DDropdownItem
                                    :id="`action_ignore_${item.id}`"
                                    @click="ignoreEntity(item)"
                                >
                                    {{ __('administrations.sanity_checks.modals.ignore_item') }}
                                </DDropdownItem>

                                <DDropdownItem
                                    v-if="sanityCheck.can_be_fixed && sanityCheck.name === 'Customers without Cloudpit accounts'"
                                    :id="`action_fix_${item.id}`"
                                    @click="handleCloudpitModalClick(item)"
                                >
                                    {{ __('administrations.sanity_checks.modals.fix_item') }}
                                </DDropdownItem>

                                <DDropdownItem
                                    v-else-if="sanityCheck.can_be_fixed && sanityCheck.name === 'Webcards'"
                                    :id="`action_fix_${item.id}`"
                                    @click="handleWebCardModalClick(item)"
                                >
                                    {{ __('administrations.sanity_checks.modals.fix_item') }}
                                </DDropdownItem>

                                <DDropdownItem
                                    v-else-if="sanityCheck.can_be_fixed"
                                    :id="`action_fix_${item.id}`"
                                    @click="fixEntity(item)"
                                >
                                    {{ __('administrations.sanity_checks.modals.fix_item') }}
                                </DDropdownItem>
                            </DDropdown>
                        </div>
                    </td>
                </template>
                <template #messages="{ item }">
                    <td class="p-0">
                        <DCollapse :visible="isErrorAlertVisible(item)">
                            <DCardBody>
                                <DAlert
                                    :visible="isErrorAlertVisible(item)"
                                    dismissible
                                    color="danger"
                                    class="m-0"
                                    @close="resetSelectedItem()"
                                >
                                    {{ selectedAffectedObject?.message }}
                                </DAlert>
                            </DCardBody>
                        </DCollapse>
                    </td>
                </template>
            </DTable>
        </DCard>
    </DModal>
    <CloudpitWelcomeEmailModal
        v-if="showCreateCloudpitCustomerModal"
        :show-modal="showCreateCloudpitCustomerModal"
        :item="selectedAffectedObject"
        @close-modal="handleCloudpitModalClose"
        @confirm="handleCloudpitModalConfirm"
    />
    <WebCardModal
        v-if="showWebCardModal"
        :show-modal="showWebCardModal"
        :item="selectedAffectedObject"
        @close-modal="handleWebCardModalClose"
        @confirm="handleWebCardModalConfirm"
    />
</template>

<script>

import CloudpitWelcomeEmailModal from './CloudpitWelcomeEmailModal.vue'
import WebCardModal from '~@/Components/Administrations/SanityChecks/WebCardModal.vue'

const classMapping = {
    invoice: {
        permission: 'invoice.view',
        route: 'customers.customers.invoices.show',
        parameters: {
            customer: 'customer_id',
            invoice: 'id'
        }
    },
    contract: {
        permission: 'contract.view',
        route: 'customers.customers.contracts.show',
        parameters: {
            customer: 'customer_id',
            contract: 'id'
        }
    },
    product: {
        permission: 'product.view',
        route: 'products.products.show',
        parameters: {
            product: 'id'
        }
    },
    credit_note: {
        permissions: 'credit_note.manage',
        route: 'customers.customers.credit-notes.show',
        parameters: {
            customer: 'customer_id',
            credit_note: 'id'
        }
    },
    customer: {
        permission: 'customer.view',
        route: 'customers.customers.show',
        parameters: {
            customer: 'id'
        }
    },
    transaction: {
        permission: 'customer.view',
        route: 'customers.customers.transactions.index',
        parameters: {
            customer: 'customer_id',
            search: 'id'
        }
    }
}

export default {
    name: 'AffectedObjectsModal',
    components: { WebCardModal, CloudpitWelcomeEmailModal },
    props: {
        showModal: {
            type: Boolean,
            default: false
        },
        sanityCheck: {
            type: Object,
            default: () => { return {} }
        }
    },
    emits: ['close-modal', 'update-affected-objects'],
    data () {
        return {
            fields: [
                { key: 'class', label: this.__('administrations.sanity_checks.modals.table.affected_object'), _style: { width: '30%' } },
                { key: 'actions', label: this.__('administrations.sanity_checks.modals.table.actions'), _style: { width: '20%' } },
                { key: 'messages', label: this.__('administrations.sanity_checks.modals.table.messages'), _style: { width: '50%' } }
            ],
            affectedObjectData: this.sanityCheck.affected_objects,
            modalHasErrors: false,
            error: null,
            showCreateCloudpitCustomerModal: false,
            selectedAffectedObject: null,
            classMapping,
            showWebCardModal: false
        }
    },
    computed: {
        userPermissions () {
            return this.$page.props.auth.permissions
        }
    },
    methods: {
        isErrorAlertVisible (item) {
            return this.selectedAffectedObject !== null &&
                this.selectedAffectedObject.message !== null &&
                this.selectedAffectedObject.success === false &&
                this.selectedAffectedObject.id === item.id
        },
        isSuccessAlertVisible () {
            return this.selectedAffectedObject !== null &&
                this.selectedAffectedObject.message !== null &&
                this.selectedAffectedObject.success === true
        },
        setSelectedItem (item) {
            this.selectedAffectedObject = { ...item, message: null, success: true }
        },
        resetSelectedItem () {
            this.selectedAffectedObject = null
        },
        updateAffectedObjectData (sanityChecks) {
            this.affectedObjectData = sanityChecks
        },
        classLinkable (item) {
            return this.classMapping[item.class] !== undefined &&
                this.userPermissions.includes(this.classMapping[item.class].permission)
        },
        classLink (item) {
            const classMappingElement = this.classMapping[item.class]
            const routeParameters = {}

            for (const [key, value] of Object.entries(classMappingElement.parameters)) {
                routeParameters[key] = item[value] ?? 0
            }

            return this.$route(classMappingElement.route, routeParameters)
        },
        ignoreEntity (item) {
            this.setSelectedItem(item)

            window.axios.post(
                this.$route(
                    'administrations.sanity_checks.ignore_item',
                    {
                        sanity_check: this.sanityCheck.id,
                        object_id: item.id,
                        object_class: item.class
                    }
                )
            )
                .then((response) => {
                    this.selectedAffectedObject.message = response.data.message
                    this.$emit('update-affected-objects', response.data.sanity_check)
                    this.modalHasErrors = false
                    this.updateAffectedObjectData(response.data.sanity_check.affected_objects)
                })
                .catch((error) => {
                    this.selectedAffectedObject.success = false
                    this.selectedAffectedObject.message = error.response.data.error
                })
        },
        fixEntity (item, params) {
            this.setSelectedItem(item)

            window.axios.post(
                this.$route(
                    'administrations.sanity_checks.fix-item',
                    {
                        sanity_check: this.sanityCheck.id,
                        object_id: item.id,
                        object_class: item.class,
                        params: params
                    }
                )
            )
                .then((response) => {
                    this.selectedAffectedObject.message = response.data.message
                    this.$emit('update-affected-objects', response.data.sanity_check)
                    this.modalHasErrors = false
                    this.updateAffectedObjectData(response.data.sanity_check.affected_objects)
                })
                .catch((error) => {
                    this.selectedAffectedObject.success = false
                    this.selectedAffectedObject.message = error.response.data.error
                })
        },
        handleCloudpitModalConfirm (event) {
            this.fixEntity(event.item, { sendMail: event.sendMail })
        },
        handleCloudpitModalClick (item) {
            this.showCreateCloudpitCustomerModal = true
            this.selectedAffectedObject = item
        },
        handleWebCardModalConfirm (event) {
            this.fixEntity(event.item, { web_card_id: event.web_card_id, child_contract_id: event.item.child_contract_id })
        },
        handleWebCardModalClick (item) {
            this.showWebCardModal = true
            this.selectedAffectedObject = item
        },
        handleWebCardModalClose () {
            this.showWebCardModal = false
            this.resetSelectedItem()
        },
        handleCloudpitModalClose () {
            this.showCreateCloudpitCustomerModal = false
            this.resetSelectedItem()
        }
    }
}
</script>
